import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { GlowingEffect } from "./glowing-effect";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "relative bg-background text-foreground border-2 border-transparent bg-clip-padding before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:bg-gradient-to-r before:from-[#00FFFF] before:to-[#BF00FF] before:p-[2px] hover:shadow-[0_0_20px_rgba(0,255,255,0.3)] active:scale-[0.98] transition-all duration-300",
        destructive: "bg-destructive text-destructive-foreground border border-destructive/30 hover:bg-destructive/90 hover:shadow-[0_0_15px_hsl(var(--destructive)/0.3)]",
        outline: "border-2 border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground hover:border-border/80",
        secondary: "bg-secondary text-secondary-foreground border border-border hover:bg-secondary/80",
        ghost: "hover:bg-accent text-muted-foreground hover:text-accent-foreground",
        link: "text-foreground underline-offset-4 hover:underline hover:text-primary",
        "gradient-purple": "bg-gradient-to-r from-[#00FFFF] to-[#BF00FF] text-white border-none hover:shadow-[0_0_20px_rgba(0,255,255,0.5)] active:scale-[0.98] transition-all duration-300",
      },
      size: {
        default: "h-10 px-6 py-2",
        sm: "h-9 rounded-lg px-4 text-xs",
        lg: "h-12 rounded-xl px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  withGlow?: boolean;
  glowProps?: {
    blur?: number;
    proximity?: number;
    spread?: number;
  };
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, withGlow = false, glowProps, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }), withGlow && "relative")} ref={ref} {...props}>
        {withGlow && <GlowingEffect proximity={200} spread={30} {...glowProps} />}
        {props.children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
